import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Container,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import useCRM from "../hooks/useCRM";
import ClientList from "../components/ClientList";
import ClientForm from "../components/ClientForm";
import LeadForm from "../components/LeadForm";
import ProjectForm from "../components/ProjectForm";
import CRMManager from "../components/CRMManager";
import Loader from "../../../components/common/Loader";
import Modal from "../../../components/common/Modal";

const CRMPage = () => {
  const {
    clients,
    selectedClient,
    clientFilters,
    leads,
    selectedLead,
    leadFilters,
    projects,
    selectedProject,
    projectFilters,
    tasks,
    interactions,
    loading,
    error,
    success,
    getClients,
    getClientById,
    createNewClient,
    updateExistingClient,
    deleteExistingClient,
    getLeads,
    getLeadById,
    createNewLead,
    updateExistingLead,
    deleteExistingLead,
    convertExistingLeadToClient,
    getProjects,
    getProjectById,
    createNewProject,
    updateExistingProject,
    deleteExistingProject,
    getTasks,
    createNewTask,
    createNewInteraction,
    clearErrorState,
    clearSuccessState,
    updateClientFilters,
    updateLeadFilters,
    updateProjectFilters,
    clearSelectedClient,
    clearSelectedLead,
    clearSelectedProject,
  } = useCRM();

  const [moduleTab, setModuleTab] = useState(0);
  const [viewMode, setViewMode] = useState("list");
  const [loadingAction, setLoadingAction] = useState(false);

  const [showClientModal, setShowClientModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editMode, setEditMode] = useState(null);

  useEffect(() => {
    getClients(clientFilters);
    getLeads(leadFilters);
    getProjects(projectFilters);
    getTasks();
  }, []);

  useEffect(() => {
    getClients(clientFilters);
  }, [clientFilters]);

  useEffect(() => {
    getLeads(leadFilters);
  }, [leadFilters]);

  useEffect(() => {
    getProjects(projectFilters);
  }, [projectFilters]);

  const currentMode = useMemo(() => {
    if (moduleTab === 0) return "client";
    if (moduleTab === 1) return "lead";
    return "project";
  }, [moduleTab]);

  const selectedRecord = useMemo(() => {
    if (currentMode === "client") return selectedClient;
    if (currentMode === "lead") return selectedLead;
    return selectedProject;
  }, [currentMode, selectedClient, selectedLead, selectedProject]);

  const relatedTasks = useMemo(() => {
    if (!selectedRecord) return [];
    return tasks.filter(
      (task) =>
        task.clientId === selectedRecord.id ||
        task.leadId === selectedRecord.id ||
        task.projectId === selectedRecord.id,
    );
  }, [tasks, selectedRecord]);

  const relatedInteractions = useMemo(() => {
    if (!selectedRecord) return [];
    return interactions.filter(
      (item) =>
        item.clientId === selectedRecord.id ||
        item.leadId === selectedRecord.id ||
        item.projectId === selectedRecord.id,
    );
  }, [interactions, selectedRecord]);

  const withLoading = async (fn) => {
    setLoadingAction(true);
    try {
      await fn();
      clearErrorState();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleBackToList = () => {
    setViewMode("list");
    clearSelectedClient();
    clearSelectedLead();
    clearSelectedProject();
  };

  const handleSelect = async (id) => {
    if (currentMode === "client") await getClientById(id);
    if (currentMode === "lead") await getLeadById(id);
    if (currentMode === "project") await getProjectById(id);
    setViewMode("details");
  };

  const handleCreateClient = (data) =>
    withLoading(async () => {
      await createNewClient(data);
      setShowClientModal(false);
      await getClients(clientFilters);
    });

  const handleCreateLead = (data) =>
    withLoading(async () => {
      await createNewLead(data);
      setShowLeadModal(false);
      await getLeads(leadFilters);
    });

  const handleCreateProject = (data) =>
    withLoading(async () => {
      await createNewProject(data);
      setShowProjectModal(false);
      await getProjects(projectFilters);
    });

  const handleEditSubmit = (data) =>
    withLoading(async () => {
      if (editMode === "client" && selectedClient) {
        await updateExistingClient(selectedClient.id, data);
        await getClientById(selectedClient.id);
        await getClients(clientFilters);
      }
      if (editMode === "lead" && selectedLead) {
        await updateExistingLead(selectedLead.id, data);
        await getLeadById(selectedLead.id);
        await getLeads(leadFilters);
      }
      if (editMode === "project" && selectedProject) {
        await updateExistingProject(selectedProject.id, data);
        await getProjectById(selectedProject.id);
        await getProjects(projectFilters);
      }
      setShowEditModal(false);
      setEditMode(null);
    });

  const handleDelete = async () => {
    if (!selectedRecord) return;
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    await withLoading(async () => {
      if (currentMode === "client") {
        await deleteExistingClient(selectedRecord.id);
        await getClients(clientFilters);
      }
      if (currentMode === "lead") {
        await deleteExistingLead(selectedRecord.id);
        await getLeads(leadFilters);
      }
      if (currentMode === "project") {
        await deleteExistingProject(selectedRecord.id);
        await getProjects(projectFilters);
      }
      handleBackToList();
    });
  };

  const handleCreateTask = async () => {
    if (!selectedRecord) return;
    const title = window.prompt("Task title");
    if (!title) return;

    await withLoading(async () => {
      await createNewTask({
        title,
        clientId: currentMode === "client" ? selectedRecord.id : "",
        leadId: currentMode === "lead" ? selectedRecord.id : "",
        projectId: currentMode === "project" ? selectedRecord.id : "",
        priority: "medium",
        status: "pending",
      });
      await getTasks();
    });
  };

  const handleCreateInteraction = async () => {
    if (!selectedRecord) return;
    const title = window.prompt("Interaction title");
    if (!title) return;

    await withLoading(async () => {
      await createNewInteraction({
        title,
        type: "call",
        direction: "outgoing",
        clientId: currentMode === "client" ? selectedRecord.id : "",
        leadId: currentMode === "lead" ? selectedRecord.id : "",
        projectId: currentMode === "project" ? selectedRecord.id : "",
      });
    });
  };

  const handleConvertLead = async () => {
    if (!selectedLead) return;

    await withLoading(async () => {
      const newClient = await createNewClient({
        fullName: selectedLead.fullName,
        firstName: selectedLead.firstName,
        lastName: selectedLead.lastName,
        email: selectedLead.email,
        phone: selectedLead.phone,
        whatsapp: selectedLead.whatsapp,
        type: selectedLead.leadType === "wedding" ? "wedding" : "individual",
        stage: "booked",
        notesSummary: selectedLead.message,
      });

      await convertExistingLeadToClient(selectedLead.id, newClient.id);
      await getLeads(leadFilters);
      await getClients(clientFilters);
      handleBackToList();
      setModuleTab(0);
    });
  };

  const renderList = () => {
    if (moduleTab === 0) {
      return (
        <ClientList
          clients={clients}
          onSelect={handleSelect}
          onEdit={(id) => handleSelect(id)}
          onDelete={async (id) => {
            if (!window.confirm("Delete client?")) return;
            await withLoading(async () => {
              await deleteExistingClient(id);
              await getClients(clientFilters);
            });
          }}
          filters={clientFilters}
          onFilterChange={updateClientFilters}
          loading={loading}
        />
      );
    }

    if (moduleTab === 1) {
      return (
        <ClientList
          clients={leads.map((lead) => ({
            ...lead,
            fullName: lead.fullName,
            type: lead.leadType,
            clientCode: lead.leadCode,
          }))}
          onSelect={handleSelect}
          onEdit={(id) => handleSelect(id)}
          onDelete={async (id) => {
            if (!window.confirm("Delete lead?")) return;
            await withLoading(async () => {
              await deleteExistingLead(id);
              await getLeads(leadFilters);
            });
          }}
          filters={leadFilters}
          onFilterChange={updateLeadFilters}
          loading={loading}
        />
      );
    }

    return (
      <ClientList
        clients={projects.map((project) => ({
          ...project,
          fullName: project.title,
          phone: project.clientId,
          email: project.event?.venue?.name || "",
          type: project.type,
          clientCode: project.projectCode,
        }))}
        onSelect={handleSelect}
        onEdit={(id) => handleSelect(id)}
        onDelete={async (id) => {
          if (!window.confirm("Delete project?")) return;
          await withLoading(async () => {
            await deleteExistingProject(id);
            await getProjects(projectFilters);
          });
        }}
        filters={projectFilters}
        onFilterChange={updateProjectFilters}
        loading={loading}
      />
    );
  };

  const renderEditForm = () => {
    if (editMode === "client") {
      return (
        <ClientForm
          initialData={selectedClient}
          onSubmit={handleEditSubmit}
          onCancel={() => setShowEditModal(false)}
          loading={loadingAction}
        />
      );
    }
    if (editMode === "lead") {
      return (
        <LeadForm
          initialData={selectedLead}
          onSubmit={handleEditSubmit}
          onCancel={() => setShowEditModal(false)}
          loading={loadingAction}
        />
      );
    }
    return (
      <ProjectForm
        initialData={selectedProject}
        onSubmit={handleEditSubmit}
        onCancel={() => setShowEditModal(false)}
        loading={loadingAction}
      />
    );
  };

  if (loading && !clients.length && !leads.length && !projects.length) {
    return <Loader fullScreen />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            CRM
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage clients, leads, and photography projects
          </Typography>
        </Box>

        {viewMode === "list" && (
          <>
            {moduleTab === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowClientModal(true)}
              >
                New Client
              </Button>
            )}
            {moduleTab === 1 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowLeadModal(true)}
              >
                New Lead
              </Button>
            )}
            {moduleTab === 2 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowProjectModal(true)}
              >
                New Project
              </Button>
            )}
          </>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={clearErrorState}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={clearSuccessState}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {success}
        </Alert>
      )}

      {viewMode === "list" && (
        <>
          <Tabs
            value={moduleTab}
            onChange={(e, v) => setModuleTab(v)}
            sx={{ mb: 3 }}
          >
            <Tab label="Clients" />
            <Tab label="Leads" />
            <Tab label="Projects" />
          </Tabs>
          {renderList()}
        </>
      )}

      {viewMode === "details" && selectedRecord && (
        <Box>
          <Button
            onClick={handleBackToList}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 3 }}
          >
            Back
          </Button>

          <CRMManager
            mode={currentMode}
            record={selectedRecord}
            tasks={relatedTasks}
            interactions={relatedInteractions}
            onEdit={() => {
              setEditMode(currentMode);
              setShowEditModal(true);
            }}
            onDelete={handleDelete}
            onCreateTask={handleCreateTask}
            onCreateInteraction={handleCreateInteraction}
            onConvertLead={
              currentMode === "lead" ? handleConvertLead : undefined
            }
          />
        </Box>
      )}

      <Modal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
        title="Create Client"
      >
        <ClientForm
          onSubmit={handleCreateClient}
          onCancel={() => setShowClientModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        title="Create Lead"
      >
        <LeadForm
          onSubmit={handleCreateLead}
          onCancel={() => setShowLeadModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        title="Create Project"
      >
        <ProjectForm
          onSubmit={handleCreateProject}
          onCancel={() => setShowProjectModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Record"
      >
        {renderEditForm()}
      </Modal>
    </Container>
  );
};

export default CRMPage;
